import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface SkipLinkProps {
	/** The id of the element to move focus to. Must carry `tabIndex={-1}`. */
	targetId: string;
}

/**
 * "Skip to main content" link (WCAG 2.4.1 — Bypass Blocks).
 *
 * Rendered as the first focusable element on the page so a keyboard user's very
 * first Tab reveals it. Activating it (Enter/click) moves focus past the sidebar
 * navigation straight into the page's main content region, so users don't have to
 * tab through every nav item on every page. It is visually hidden (translated out
 * of view) until focused, then slides into the top-left corner.
 */
const SkipLink = ({ targetId }: SkipLinkProps) => {
	const { t } = useTranslation();

	const focusTarget = (event: React.SyntheticEvent) => {
		const target = document.getElementById(targetId);
		if (!target) return;
		// The target carries tabIndex={-1} so it is programmatically focusable
		// without joining the tab order. Move focus there and scroll it into view.
		event.preventDefault();
		target.focus();
		target.scrollIntoView({ block: 'start' });
	};

	return (
		<Box
			component="a"
			href={`#${targetId}`}
			onClick={focusTarget}
			sx={{
				position: 'fixed',
				top: 8,
				left: 8,
				zIndex: (theme) => theme.zIndex.tooltip + 1,
				px: 2,
				py: 1,
				borderRadius: 1.5,
				bgcolor: 'primary.main',
				color: 'primary.contrastText',
				fontWeight: 600,
				fontSize: '0.875rem',
				lineHeight: 1.4,
				textDecoration: 'none',
				boxShadow: 4,
				// Hidden until focused: pushed above the viewport while still focusable,
				// then slid into view when it (or its descendants) receive keyboard focus.
				transform: 'translateY(calc(-100% - 16px))',
				transition: 'transform 0.15s ease',
				'&:focus, &:focus-visible': {
					transform: 'translateY(0)',
					outline: (theme) => `2px solid ${theme.palette.primary.contrastText}`,
					outlineOffset: 2
				}
			}}
		>
			{t('common.skipToContent', 'Skip to main content')}
		</Box>
	);
};

export default SkipLink;
